import moment from 'moment'

/**
 * Sanitizes and normalizes date strings before parsing
 * Handles common malformed date formats from API
 * @param {string} dateString - The date string to sanitize
 * @returns {string|null} - Sanitized date string or null
 */
export function sanitizeDateString(dateString) {
  if (!dateString) return null

  let sanitized = String(dateString).trim()

  // Remove duplicate timezone information
  // Pattern: "2025-12-13 07:20:38.00418 +0000 +0000" -> "2025-12-13 07:20:38.00418 +0000"
  sanitized = sanitized.replace(/([+-]\d{4})\s+\1/g, '$1')

  // Remove duplicate timezone at end (more specific pattern)
  sanitized = sanitized.replace(/([+-]\d{4})\s+([+-]\d{4})$/g, '$1')

  // Clean up microseconds if present (moment doesn't handle > 6 digits well)
  // Pattern: ".00418" -> ".004180" (pad to 6 digits) or truncate if > 6
  sanitized = sanitized.replace(/\.(\d{1,6})\d*/g, (match, digits) => {
    return '.' + digits.padEnd(6, '0').substring(0, 6)
  })

  return sanitized
}

/**
 * Safely parses a date string with fallback handling
 * @param {string} dateString - The date string to parse
 * @param {string} format - Optional format string for strict parsing
 * @returns {moment.Moment|null} - Parsed moment object or null if invalid
 */
export function parseDate(dateString, format = null) {
  if (!dateString) return null

  const sanitized = sanitizeDateString(dateString)

  if (!sanitized) return null

  // Try parsing with format if provided
  if (format) {
    const parsed = moment(sanitized, format, true)
    if (parsed.isValid()) return parsed
  }

  // Try parsing as ISO format
  const isoParsed = moment(sanitized, moment.ISO_8601, true)
  if (isoParsed.isValid()) return isoParsed

  // Try RFC 2822 format (e.g., "Sat, 13 Dec 2025 01:23:45 +0000")
  // This is commonly used in HTTP headers and email dates
  const rfc2822Parsed = moment(sanitized, 'ddd, DD MMM YYYY HH:mm:ss ZZ', true)
  if (rfc2822Parsed.isValid()) return rfc2822Parsed

  // Try RFC 2822 without day name (e.g., "13 Dec 2025 01:23:45 +0000")
  const rfc2822NoDayParsed = moment(sanitized, 'DD MMM YYYY HH:mm:ss ZZ', true)
  if (rfc2822NoDayParsed.isValid()) return rfc2822NoDayParsed

  // Try parsing as standard formats (common API formats)
  const standardFormats = [
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm:ss.SSS',
    'YYYY-MM-DD HH:mm:ss.SSSSSS',
    'YYYY-MM-DD HH:mm:ss Z',
    'YYYY-MM-DD HH:mm:ss.SSS Z',
    'YYYY-MM-DD HH:mm:ss.SSSSSS Z',
    'YYYY-MM-DD HH:mm:ss.SSSSSS',
    'YYYY-MM-DD HH:mm:ss.SSSSSS Z',
    // Additional variations with timezone
    'ddd, DD MMM YYYY HH:mm:ss Z',
    'DD MMM YYYY HH:mm:ss Z'
  ]

  const standardParsed = moment(sanitized, standardFormats, true)

  if (standardParsed.isValid()) return standardParsed

  // Try native Date parsing for Safari compatibility (Safari handles RFC 2822 natively)
  // This is more reliable than moment's fallback for certain formats
  const nativeDate = new Date(sanitized)
  if (!isNaN(nativeDate.getTime())) {
    const nativeMoment = moment(nativeDate)
    if (nativeMoment.isValid()) {
      return nativeMoment
    }
  }

  // Last resort: try moment's default parsing (suppress warning by using strict mode first)
  const fallback = moment(sanitized)
  if (fallback.isValid()) {
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      // console.warn(
      //   'Date parsed with fallback (may be unreliable):',
      //   dateString,
      //   '->',
      //   sanitized
      // )
    }
    return fallback
  }

  // Invalid date - log error in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Invalid date format:', dateString)
  }

  return null
}

/**
 * Formats a date safely
 * @param {string} dateString - The date string to format
 * @param {string} format - Output format (default: 'DD-MM-YYYY')
 * @returns {string} - Formatted date string or original string if invalid
 */
export function formatDate(dateString, format = 'DD-MM-YYYY') {
  if (!dateString) return ''

  const parsed = parseDate(dateString)

  if (!parsed || !parsed.isValid()) {
    // Return original string if can't parse (better than empty)
    return String(dateString)
  }

  return parsed.format(format)
}

/**
 * Formats a date with time safely
 * @param {string} dateString - The date string to format
 * @param {string} format - Output format (default: 'DD-MM-YYYY HH:mm:ss')
 * @returns {string} - Formatted date string or original string if invalid
 */
export function formatDateTime(dateString, format = 'DD-MM-YYYY HH:mm:ss') {
  return formatDate(dateString, format)
}
